/**
 * Route patterns for help: which router paths exist (read from src/app/router.tsx at build time),
 * which source folder renders each one (for the PR review and the staleness warning), and how a
 * section's `routes` match the page the user is on. Pure functions: no DOM, no fs.
 */

export interface RouterRoute {
  /** Absolute pattern as the user sees it: `/`, `/payments`, `/shipments/:id`. */
  path: string;
  /** The element's component, e.g. `PaymentsView`. */
  component: string | null;
  /** The component's file relative to the repo root, e.g. `src/features/payments/PaymentsView`. */
  file: string | null;
}

/** `/shipments/:id/` → `/shipments/:id`; `` → `/`. */
export function normalisePath(path: string): string {
  const trimmed = path.trim().replace(/\/+$/, '');
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

function resolveImport(from: string, spec: string): string {
  const parts = from.split('/').slice(0, -1);
  for (const seg of spec.split('/')) {
    if (seg === '..') parts.pop();
    else if (seg !== '.') parts.push(seg);
  }
  return parts.join('/');
}

/**
 * Reads the route table out of the router source. Top-level paths start with `/`; children of the
 * `/` layout are relative; `index: true` is `/`; the catch-all `*` is skipped. A regex over the source
 * (not an import) so the build can run it without the app's runtime; router.help.test.tsx checks it
 * against the real router so the two cannot disagree.
 */
export function parseRouter(source: string, routerFile = 'src/app/router.tsx'): RouterRoute[] {
  const imports = new Map<string, string>();
  for (const m of source.matchAll(/import\s*\{([^}]+)\}\s*from\s*'([^']+)'/g)) {
    for (const name of m[1].split(',').map((n) => n.trim()).filter(Boolean)) {
      imports.set(name, resolveImport(routerFile, m[2]));
    }
  }
  const routes: RouterRoute[] = [];
  const seen = new Set<string>();
  const entry = /\{\s*(?:path:\s*(['"])([^'"]+)\1|index:\s*true)\s*,\s*element:\s*(?:<RequireAuth>\s*)?<(\w+)/g;
  for (const m of source.matchAll(entry)) {
    const raw = m[2] ?? '';
    if (raw === '*') continue;
    const path = normalisePath(raw);
    if (seen.has(path)) continue;
    seen.add(path);
    const component = m[3] ?? null;
    routes.push({ path, component, file: (component && imports.get(component)) ?? null });
  }
  // The `/` layout itself (AppShell) carries its children; index: true already gave `/` its page.
  return routes.filter((r) => !(r.path === '/' && r.component === 'RequireAuth'));
}

/** Is `pattern` one of the router's paths (param names may differ: `/shipments/:shipmentId` = `/shipments/:id`)? */
export function routeExists(pattern: string, routerPaths: string[]): boolean {
  if (pattern === '*') return true;
  const shape = (p: string) => normalisePath(p).replace(/:[^/]+/g, ':');
  const want = shape(pattern);
  return routerPaths.some((p) => shape(p) === want);
}

/**
 * How specifically `pattern` matches `pathname`: null if it does not; otherwise higher = more specific
 * (more segments, then more literal segments). `*` matches everything with the lowest score.
 */
export function matchRoute(pattern: string, pathname: string): number | null {
  if (pattern === '*') return 0;
  const want = normalisePath(pattern).split('/').filter(Boolean);
  const got = normalisePath(pathname).split('/').filter(Boolean);
  if (want.length !== got.length) return null;
  let literal = 0;
  for (let i = 0; i < want.length; i += 1) {
    if (want[i].startsWith(':')) {
      if (!got[i]) return null;
    } else if (want[i].toLowerCase() !== decodeURIComponent(got[i]).toLowerCase()) {
      return null;
    } else {
      literal += 1;
    }
  }
  return 1 + want.length * 10 + literal;
}

/** Does a concrete path (`/shipments/108`) land on one of the router's patterns? */
export function pathIsRouted(pathname: string, routerPaths: string[]): boolean {
  return routerPaths.some((p) => matchRoute(p, pathname) !== null);
}

/** The folder whose changes can change what a route shows: its component's directory. */
export function routeSourceDir(route: RouterRoute): string | null {
  if (!route.file) return null;
  return route.file.split('/').slice(0, -1).join('/') + '/';
}

/** Source folders behind a section's routes (`*` and unknown routes contribute nothing). */
export function sourceDirsForRoutes(routes: string[], table: RouterRoute[]): string[] {
  const dirs = new Set<string>();
  for (const pattern of routes) {
    if (pattern === '*') continue;
    for (const r of table) {
      if (routeExists(pattern, [r.path])) {
        const dir = routeSourceDir(r);
        if (dir) dirs.add(dir);
      }
    }
  }
  return [...dirs].sort();
}
